import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
}

/**
 * Dynamically updates <head> meta tags for the current page.
 * This is critical for SPAs where all routes share a single index.html —
 * the index.html canonical always points to the homepage, so without this
 * Google marks every inner page as "Alternate page with proper canonical tag".
 */
const SEOHead = ({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage = 'https://sinisterfiles.com/og-image.jpg',
  ogType = 'website',
}: SEOHeadProps) => {
  useEffect(() => {
    // Title
    document.title = title;

    // Description
    setMeta('name', 'description', description);

    // Canonical
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonical);

    // Open Graph
    setMetaProp('og:url', canonical);
    setMetaProp('og:title', ogTitle || title);
    setMetaProp('og:description', ogDescription || description);
    setMetaProp('og:image', ogImage);
    setMetaProp('og:type', ogType);

    // Twitter
    setMeta('name', 'twitter:title', ogTitle || title);
    setMeta('name', 'twitter:description', ogDescription || description);
    setMeta('name', 'twitter:image', ogImage);
  }, [title, description, canonical, ogTitle, ogDescription, ogImage, ogType]);

  return null;
};

function setMeta(attrKey: string, attrValue: string, content: string) {
  let el = document.querySelector(`meta[${attrKey}="${attrValue}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrKey, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setMetaProp(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export default SEOHead;
